import type { DisplayLanguage } from '@amzn/journeys-common';
import {
  type Campaign,
  CampaignState,
  type Placement,
  useApproveCampaignMutation,
  useArchiveCampaignMutation,
  useCancelApproveCampaignMutation,
  useCloneCampaignMutation,
  useCreateCampaignMutation,
  usePublishCampaignMutation,
  useRequestApproveCampaignMutation,
  useRestoreCampaignMutation,
  useSaveCampaignMutation,
  useSuspendCampaignMutation,
} from 'generated/graphql';
import type { GraphQLFormattedError } from 'graphql';
import { useUserData } from 'hooks/use-user-data';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-final-form';
import { useHistory, useParams } from 'react-router-dom';
import { omitDeep } from 'utils/lodash-replacers';
import { generateId } from '../../../app.util';
import { journeysCampaignPagePath } from '../../../components/Paths';
import type { CampaignFormValues, CampaignMenuItem, PreviewUrl } from '../campaign.types';
import {
  approveMenuItemId,
  archiveMenuItemId,
  cancelApprovalRequestItemId,
  canUserApproveTheirCampaign,
  cbrApproveMenuItemId,
  cloneMenuItemId,
  getCampaignBadges,
  getCampaignMenu,
  getLanguageOptionFromDisplayLanguage,
  hasPreviews,
  isUserCBR,
  previewCombinationsMenuItemId,
  publishToSymphonyMenuItemId,
  requestApprovalMenuItemId,
  restoreMenuItemId,
  saveMenuItemId,
  suspendMenuItemId,
} from '../campaign.util';
import type { CampaignErrorMessages } from './CampaignErrorsTypes';
import {
  getApproveCampaignInput,
  getArchiveCampaignInput,
  getCancelApproveCampaignInput,
  getCloneCampaignInput,
  getCreateCampaignInput,
  getPublishCampaignInput,
  getRequestApproveCampaignInput,
  getRestoreCampaignInput,
  getSaveCampaignInput,
  getSuspendCampaignInput,
} from './CampaignSidebar.util';
import { CampaignSidebarLayout } from './CampaignSidebarLayout';
import { MVTApprovalModal } from './MVTApprovalModal';
import { MVTCombinationsModal } from './MVTCombinationsModal';
import { SaveAndProceedModal } from './SaveAndProceedModal';

export type CampaignSidebarProps = {
  onError: (errors: CampaignErrorMessages) => void;
  campaignQueryPolling: () => void;
};

export const CampaignSidebar: FC<CampaignSidebarProps> = ({ onError, campaignQueryPolling }) => {
  const form = useForm<CampaignFormValues>();
  const history = useHistory();
  const { campaignId } = useParams<{
    campaignId?: string;
  }>();
  const config = form.getState().values.config;
  const campaign = form.getState().values.campaign;
  const projectEvent = form.getState().values.projectEvent;
  const [createCampaignMutation] = useCreateCampaignMutation();
  const [saveCampaignMutation] = useSaveCampaignMutation();
  const [publishCampaignMutation] = usePublishCampaignMutation();
  const [requestApproveCampaignMutation] = useRequestApproveCampaignMutation();
  const [cancelApproveCampaignMutation] = useCancelApproveCampaignMutation();
  const [approveCampaignMutation] = useApproveCampaignMutation();
  const [cloneCampaignMutation] = useCloneCampaignMutation();
  const [archiveCampaignMutation] = useArchiveCampaignMutation();
  const [restoreCampaignMutation] = useRestoreCampaignMutation();
  const [suspendCampaignMutation] = useSuspendCampaignMutation();
  const [showMVTApprovalModal, setShowMVTApprovalModal] = useState(false);
  const [showSaveAndProceedModal, setShowSaveAndProceedModal] = useState(false);
  const [handleOnSaveAndProceed, setHandleOnSaveAndProceed] = useState(() => () => {});
  const [showMVTCombinationsModal, setShowMVTCombinationsModal] = useState(false);
  const [mvtCombinationsModalTitle, setMVTCombinationsModalTitle] = useState('');
  const [mvtCombinationsModalActionLabel, setMVTCombinationsModalActionLabel] = useState<string>();
  const [handleMVTCombinationsModalOnAction, setHandleMVTCombinationsModalOnAction] = useState<() => () => void>();

  const { alias, groups } = useUserData();

  const startCampaignQueryPolling = () => {
    if (!campaignId) {
      redirectToNewCampaign();
    } else {
      campaignQueryPolling();
    }
  };

  const processErrors = (graphQLErrors?: readonly GraphQLFormattedError[], error?: Error) => {
    const campaignErrorMessages: CampaignErrorMessages = graphQLErrors?.map((e) => e) ?? [];
    if (error) {
      campaignErrorMessages.push({
        message: error.message,
      });
    }
    onError(campaignErrorMessages);
  };

  const setIsLoading = (isLoading: boolean) => {
    form.change('config.isLoading' as keyof CampaignFormValues, isLoading as unknown as CampaignFormValues[keyof CampaignFormValues]);
  };

  const setUserVersion = (version?: number) => {
    if (version) {
      form.change('campaign.userVersion' as keyof CampaignFormValues, version as unknown as CampaignFormValues[keyof CampaignFormValues]);
    }
  };

  const setCampaignState = (campaignState: CampaignState) => {
    form.change('campaign.state' as keyof CampaignFormValues, campaignState as unknown as CampaignFormValues[keyof CampaignFormValues]);
  };

  const setIsArchived = (isArchived: boolean) => {
    form.change('campaign.isArchived' as keyof CampaignFormValues, isArchived as unknown as CampaignFormValues[keyof CampaignFormValues]);
  };

  const redirectToNewCampaign = () => {
    const campaign = form.getState().values.campaign;
    const updatedUrl = journeysCampaignPagePath.replace(':projectId', campaign.projectId).replace(':campaignId', campaign.id);
    if (campaign.projectId && campaign.id) {
      history.push(updatedUrl);
    }
  };

  const saveCampaign = (callback?: (success: boolean) => void) => {
    const campaign = form.getState().values.campaign;
    const config = form.getState().values.config;
    let success = true;
    setIsLoading(true);
    if (config.isNew) {
      createCampaignMutation(getCreateCampaignInput(campaign, config.campaignTypeVersion))
        .then(({ data, errors }) => {
          if (errors) {
            success = false;
            processErrors(errors);
          } else {
            form.change('config.isNew' as keyof CampaignFormValues, false as unknown as CampaignFormValues[keyof CampaignFormValues]);
            if (data?.createCampaign.campaign) {
              const cleanedCampaign = omitDeep(data.createCampaign.campaign, [
                '__typename',
              ]) as Campaign;
              form.change('campaign', cleanedCampaign);
            }
          }
        })
        // biome-ignore lint/suspicious/noExplicitAny: legacy code
        .catch((error: any) => {
          success = false;
          processErrors(undefined, error);
        })
        .finally(() => {
          setIsLoading(false);
          form.initialize(form.getState().values);
          // biome-ignore lint/nursery/noUnnecessaryConditions: legacy code
          if (callback) {
            callback(success);
          } else {
            redirectToNewCampaign();
          }
        });
    } else {
      saveCampaignMutation(getSaveCampaignInput(campaign, config.campaignTypeVersion))
        .then(({ data, errors }) => {
          if (errors) {
            success = false;
            processErrors(errors);
          } else {
            setUserVersion(data?.saveCampaign.campaign.userVersion);
          }
        })
        // biome-ignore lint/suspicious/noExplicitAny: legacy code
        .catch((error: any) => {
          success = false;
          processErrors(undefined, error);
        })
        .finally(() => {
          setIsLoading(false);
          form.initialize(form.getState().values);
          callback?.(success);
        });
    }
  };

  const lockCampaign = () => {
    setIsLoading(false);
    form.change('campaign.isLocked' as keyof CampaignFormValues, true as unknown as CampaignFormValues[keyof CampaignFormValues]);
    setCampaignState(CampaignState.Loading);
  };

  const publishCampaign = () => {
    const campaign = form.getState().values.campaign;
    setIsLoading(true);
    publishCampaignMutation(getPublishCampaignInput(campaign))
      .then(({ errors }) => {
        processErrors(errors);
      })
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      .catch((error: any) => {
        processErrors(undefined, error);
      })
      .finally(() => {
        lockCampaign();
        startCampaignQueryPolling();
      });
  };

  const requestApproveCampaign = () => {
    const campaign = form.getState().values.campaign;
    setIsLoading(true);
    requestApproveCampaignMutation(getRequestApproveCampaignInput(campaign))
      .then(({ errors }) => {
        processErrors(errors);
      })
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      .catch((error: any) => {
        processErrors(undefined, error);
      })
      .finally(() => {
        lockCampaign();
        startCampaignQueryPolling();
      });
  };

  const cancelApproveCampaign = () => {
    const campaign = form.getState().values.campaign;
    setIsLoading(true);
    cancelApproveCampaignMutation(getCancelApproveCampaignInput(campaign))
      .then(({ errors }) => {
        processErrors(errors);
      })
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      .catch((error: any) => {
        processErrors(undefined, error);
      })
      .finally(() => {
        lockCampaign();
        startCampaignQueryPolling();
      });
  };

  const approveCampaign = () => {
    const campaign = form.getState().values.campaign;
    setIsLoading(true);
    approveCampaignMutation(getApproveCampaignInput(campaign))
      .then(({ errors }) => {
        processErrors(errors);
      })
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      .catch((error: any) => {
        processErrors(undefined, error);
      })
      .finally(() => {
        lockCampaign();
        startCampaignQueryPolling();
      });
  };

  const cloneCampaign = () => {
    const campaign = form.getState().values.campaign;
    setIsLoading(true);
    const newCampaignId = generateId();
    cloneCampaignMutation(getCloneCampaignInput(campaign, newCampaignId))
      .then(({ data, errors }) => {
        if (errors) {
          processErrors(errors);
        } else {
          const clonedNewId = data?.cloneCampaign.id;
          if (clonedNewId && clonedNewId === newCampaignId) {
            // redirect to new campaign if successfully cloned the campaign
            window.location.href = journeysCampaignPagePath.replace(':projectId', campaign.projectId).replace(':campaignId', newCampaignId);
          }
        }
      })
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      .catch((error: any) => {
        processErrors(undefined, error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const suspendCampaign = () => {
    const campaign = form.getState().values.campaign;
    setIsLoading(true);
    suspendCampaignMutation(getSuspendCampaignInput(campaign))
      .then(({ errors }) => {
        processErrors(errors);
      })
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      .catch((error: any) => {
        processErrors(undefined, error);
      })
      .finally(() => {
        lockCampaign();
        startCampaignQueryPolling();
      });
  };

  const archiveCampaign = () => {
    const campaign = form.getState().values.campaign;
    setIsLoading(true);
    archiveCampaignMutation(getArchiveCampaignInput(campaign))
      .then(({ data, errors }) => {
        if (errors) {
          processErrors(errors);
        } else {
          setUserVersion(data?.archiveCampaign.campaign.userVersion);
        }
      })
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      .catch((error: any) => {
        processErrors(undefined, error);
      })
      .finally(() => {
        setIsArchived(true);
        setIsLoading(false);
      });
  };

  const restoreCampaign = () => {
    const campaign = form.getState().values.campaign;
    setIsLoading(true);
    restoreCampaignMutation(getRestoreCampaignInput(campaign))
      .then(({ data, errors }) => {
        if (errors) {
          processErrors(errors);
        } else {
          setUserVersion(data?.restoreCampaign.campaign.userVersion);
        }
      })
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      .catch((error: any) => {
        processErrors(undefined, error);
      })
      .finally(() => {
        setIsArchived(false);
        setIsLoading(false);
      });
  };

  const saveAndOrPublish = () => {
    if (form.getState().dirty) {
      saveCampaign((success) => {
        if (success) {
          publishCampaign();
        }
      });
    } else {
      publishCampaign();
    }
  };

  const saveAndOrRequestApproval = () => {
    if (form.getState().dirty) {
      saveCampaign((success) => {
        if (success) {
          requestApproveCampaign();
        }
      });
    } else {
      requestApproveCampaign();
    }
  };

  const handleSave = () => {
    saveCampaign();
  };

  const handleReviewAndPublish = () => {
    setMVTCombinationsModalTitle('Review MVT Combinations');
    setMVTCombinationsModalActionLabel('Publish Selected to Symphony');
    setHandleMVTCombinationsModalOnAction(() => handleMVTCombinationsModalOnActionForPublish);
    setShowMVTCombinationsModal(true);
  };

  const handleSaveAndPublish = () => {
    saveAndOrPublish();
  };

  const handlePublishToSymphony = () => {
    saveAndOrProceed(publishCampaign);
  };

  const handleCloneCampaign = () => {
    saveAndOrProceed(cloneCampaign);
  };

  const handlePreviewCombinations = () => {
    setMVTCombinationsModalTitle('Preview MVT Combinations');
    setMVTCombinationsModalActionLabel(undefined);
    setHandleMVTCombinationsModalOnAction(undefined);
    setShowMVTCombinationsModal(true);
  };

  const handleRequestApprover = () => {
    setMVTCombinationsModalTitle('Review MVT Combinations and Request Approval');
    setMVTCombinationsModalActionLabel('Request Approval');
    setHandleMVTCombinationsModalOnAction(() => handleMVTCombinationsModalOnActionForRequestApproval);
    setShowMVTCombinationsModal(true);
  };

  const saveAndOrProceed = (callback: () => void) => {
    if (form.getState().dirty) {
      setHandleOnSaveAndProceed(() => () => {
        saveCampaign((success) => {
          if (success) {
            callback();
          }
        });
      });
      setShowSaveAndProceedModal(true);
    } else {
      callback();
    }
  };

  const approve = () => {
    const campaign = form.getState().values.campaign;
    if (campaign.isMVT) {
      setShowMVTApprovalModal(true);
    } else {
      approveCampaign();
    }
  };

  const handleMVTCombinationsModalOnHide = useCallback(() => {
    setShowMVTCombinationsModal(false);
  }, []);

  const handleMVTCombinationsModalOnCancel = useCallback(() => {
    setShowMVTCombinationsModal(false);
  }, []);

  const handleMVTApprovalModalOnHide = useCallback(() => {
    setShowMVTApprovalModal(false);
  }, []);

  const handleMVTApprovalModalOnCancel = useCallback(() => {
    setShowMVTApprovalModal(false);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  const handleMVTCombinationsModalOnActionForPublish = useCallback(() => {
    saveAndOrPublish();
    handleMVTCombinationsModalOnHide();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  const handleMVTCombinationsModalOnActionForRequestApproval = useCallback(() => {
    saveAndOrRequestApproval();
    handleMVTCombinationsModalOnHide();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  const handleMVTApprovalModalOnApprove = useCallback(() => {
    approveCampaign();
    setShowMVTApprovalModal(false);
  }, []);

  const handleSaveAndProceedModalOnCancel = useCallback(() => {
    setShowSaveAndProceedModal(false);
  }, []);

  const handleSaveAndProceedModalOnSaveAndProceed = useCallback(() => {
    handleOnSaveAndProceed();
    setShowSaveAndProceedModal(false);
  }, [
    handleOnSaveAndProceed,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  const handleMenuItemClicked = useCallback(
    (menuItem: CampaignMenuItem) => {
      const campaign = form.getState().values.campaign;
      switch (menuItem.id) {
        case saveMenuItemId:
          // biome-ignore lint/style/noNestedTernary: legacy code
          campaign.state === CampaignState.NotPublished ? handleSave() : campaign.isMVT ? handleReviewAndPublish() : handleSaveAndPublish();
          return;
        case publishToSymphonyMenuItemId:
          campaign.isMVT ? handleReviewAndPublish() : handlePublishToSymphony();
          return;
        case previewCombinationsMenuItemId:
          handlePreviewCombinations();
          return;
        case requestApprovalMenuItemId:
          handleRequestApprover();
          return;
        case cancelApprovalRequestItemId:
          cancelApproveCampaign();
          return;
        case cbrApproveMenuItemId:
          approve();
          return;
        case approveMenuItemId:
          approve();
          return;
        case cloneMenuItemId:
          handleCloneCampaign();
          return;
        case archiveMenuItemId:
          archiveCampaign();
          return;
        case restoreMenuItemId:
          restoreCampaign();
          return;
        case suspendMenuItemId:
          suspendCampaign();
          return;
        default:
          console.log(`onMenuItemClicked NOT supported for ${JSON.stringify(menuItem)}`);
      }
    },
    [
      config.isNew,
      campaign,
    ],
  );

  const isPublished = campaign.state !== CampaignState.NotPublished;
  const isUserOwner = campaign.createdBy === alias;
  const languageOption = getLanguageOptionFromDisplayLanguage(campaign.language as DisplayLanguage);
  const canOwnerApprove = canUserApproveTheirCampaign(languageOption);
  const userGroups = groups ?? [];
  const isUserCbr = isUserCBR(campaign.language as DisplayLanguage, userGroups);
  const canCbrSelfApprove = isUserCbr ? (canOwnerApprove ? true : !isUserOwner) : false;
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  useEffect(() => {
    form.change('config.isUserOwner' as keyof CampaignFormValues, isUserOwner as unknown as CampaignFormValues[keyof CampaignFormValues]);
    form.change('config.isUserCbr' as keyof CampaignFormValues, isUserCbr as unknown as CampaignFormValues[keyof CampaignFormValues]);
  }, [
    isUserOwner,
    isUserCbr,
  ]);

  const getPreviewUrls = (): PreviewUrl[] => {
    const campaign = form.getState().values.campaign;
    const previewUrls: PreviewUrl[] = [];
    if (campaign.isMVT) {
      const platform = campaign.placements[0].platform;
      campaign.symphonyInfo?.forEach((symphonyInfo) => {
        symphonyInfo.placementMapping.forEach((placementMapping) => {
          if (platform && placementMapping.symphonyPreviewURL) {
            previewUrls.push({
              previewPlatform: platform,
              previewUrl: placementMapping.symphonyPreviewURL,
            });
          }
        });
      });
    } else {
      campaign.placements.forEach((placement: Placement) => {
        if (placement.platform && placement.symphonyPreviewUrl) {
          previewUrls.push({
            previewPlatform: placement.platform,
            previewUrl: placement.symphonyPreviewUrl,
          });
        }
      });
    }

    return previewUrls;
  };

  const previewUrls = getPreviewUrls();

  return (
    <div>
      <CampaignSidebarLayout
        {...{
          badges: getCampaignBadges({
            campaignLocked: !!campaign.isLocked,
            campaignState: campaign.state,
            isArchived: !!campaign.isArchived,
            liveState: campaign.liveState,
          }),
          isLoading: config.isLoading,
          isPublished,
          menu: getCampaignMenu({
            campaignLocked: !!campaign.isLocked,
            campaignState: campaign.state,
            canCbrSelfApprove,
            canPublish: true, // only used in the vue app to determine if the project is a Ghost project
            isArchived: !!campaign.isArchived,
            isFormDirty: form.getState().dirty,
            isFormValid: form.getState().valid,
            isMVT: !!campaign.isMVT,
            isNewCampaign: config.isNew,
            isUserCbr,
            isUserOwner,
            lastSavedTime: campaign.modifiedAt,
            menuItemClicked: handleMenuItemClicked,
            previewUrls,
            projectEventExistsInSymphony: !!projectEvent?.parentEventId,
            projectHasEvent: !!projectEvent,
            publishDisabledTip: "You can't publish this campaign",
            usesArchive: config.usesArchive,
            usesCampaignCopy: config.usesCampaignCopy,
            usesCbrApproval: config.usesCbrApproval,
          }),
          onMenuItemClicked: handleMenuItemClicked,
        }}
      />
      <MVTCombinationsModal
        actionLabel={mvtCombinationsModalActionLabel}
        hasPreviews={hasPreviews(previewUrls)}
        onAction={handleMVTCombinationsModalOnAction}
        onCancel={handleMVTCombinationsModalOnCancel}
        onHide={handleMVTCombinationsModalOnHide}
        show={showMVTCombinationsModal}
        title={mvtCombinationsModalTitle}
      />
      {showMVTApprovalModal && (
        <MVTApprovalModal
          hasPreviews={hasPreviews(previewUrls)}
          onApprove={handleMVTApprovalModalOnApprove}
          onCancel={handleMVTApprovalModalOnCancel}
          onHide={handleMVTApprovalModalOnHide}
        />
      )}
      {showSaveAndProceedModal && (
        <SaveAndProceedModal onCancel={handleSaveAndProceedModalOnCancel} onSaveAndProceed={handleSaveAndProceedModalOnSaveAndProceed} />
      )}
    </div>
  );
};
